import React, { useEffect, useId, useRef, ReactElement, useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useInteractionScope } from '../InteractionScope/InteractionScope';

interface TooltipProps {
  content: React.ReactNode;
  children: ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  theme?: 'light' | 'dark';
  openDelay?: number;
  closeDelay?: number;
  /** Anchor directly to an element, including an SVG path, without an HTML wrapper. */
  asChild?: boolean;
  active?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  position = 'bottom', 
  className = '',
  theme = 'dark',
  openDelay = 250,
  closeDelay = 80,
  asChild = false,
  active = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<Element | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const openTimerRef = useRef<number>(0);
  const closeTimerRef = useRef<number>(0);
  const tooltipId = useId();
  const interactionScope = useInteractionScope();
  const scopeSubscription = useRef<(() => void) | null>(null);
  const timerGeneration = useRef(0);
  const canInteract = () => active && (!interactionScope || interactionScope.isActive());
  const releaseScopeSubscription = () => {
    scopeSubscription.current?.();
    scopeSubscription.current = null;
  };
  const watchScope = () => {
    if (!interactionScope || scopeSubscription.current) return;
    scopeSubscription.current = interactionScope.subscribe(() => {
      if (interactionScope.isActive()) return;
      timerGeneration.current++;
      window.clearTimeout(openTimerRef.current);
      window.clearTimeout(closeTimerRef.current);
      setIsVisible(false);
      setIsReady(false);
      releaseScopeSubscription();
    });
  };

  const showTooltip = (delay = openDelay) => {
    if (!canInteract()) return;
    watchScope();
    const generation = ++timerGeneration.current;
    window.clearTimeout(closeTimerRef.current);
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = window.setTimeout(() => {
      if (generation !== timerGeneration.current) return;
      if (canInteract()) setIsVisible(true);
      else releaseScopeSubscription();
    }, delay);
  };
  const hideTooltip = (delay = closeDelay) => {
    timerGeneration.current++;
    window.clearTimeout(openTimerRef.current);
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setIsVisible(false);
      setIsReady(false);
      releaseScopeSubscription();
    }, delay);
  };

  useEffect(() => () => {
    timerGeneration.current++;
    window.clearTimeout(openTimerRef.current);
    window.clearTimeout(closeTimerRef.current);
    scopeSubscription.current?.();
    scopeSubscription.current = null;
  }, []);

  useLayoutEffect(() => {
    if (active) return;
    timerGeneration.current++;
    window.clearTimeout(openTimerRef.current);
    window.clearTimeout(closeTimerRef.current);
    setIsVisible(false);
    setIsReady(false);
    scopeSubscription.current?.();
    scopeSubscription.current = null;
  }, [active]);

  useLayoutEffect(() => {
    timerGeneration.current++;
    window.clearTimeout(openTimerRef.current);
    window.clearTimeout(closeTimerRef.current);
    scopeSubscription.current?.();
    scopeSubscription.current = null;
    setIsVisible(false);
    setIsReady(false);
  }, [interactionScope]);

  useLayoutEffect(() => {
    if (!isVisible) return;
    const positionTooltip = () => {
      if (!wrapperRef.current || !tooltipRef.current) return;
      const targetRect = wrapperRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const spacing = 8;
      const padding = 12;
      let effectivePosition = position;

      if (position === 'top' && targetRect.top - tooltipRect.height - spacing < padding) effectivePosition = 'bottom';
      if (position === 'bottom' && targetRect.bottom + tooltipRect.height + spacing > window.innerHeight - padding) effectivePosition = 'top';
      if (position === 'left' && targetRect.left - tooltipRect.width - spacing < padding) effectivePosition = 'right';
      if (position === 'right' && targetRect.right + tooltipRect.width + spacing > window.innerWidth - padding) effectivePosition = 'left';

      let left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
      let top = targetRect.bottom + spacing;

      if (effectivePosition === 'bottom') {
        top = targetRect.bottom + spacing;
      } else if (effectivePosition === 'top') {
        top = targetRect.top - tooltipRect.height - spacing;
      } else if (effectivePosition === 'left') {
        left = targetRect.left - tooltipRect.width - spacing;
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
      } else if (effectivePosition === 'right') {
        left = targetRect.right + spacing;
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
      }

      left = Math.min(Math.max(padding, left), Math.max(padding, window.innerWidth - tooltipRect.width - padding));
      top = Math.min(Math.max(padding, top), Math.max(padding, window.innerHeight - tooltipRect.height - padding));

      setCoords({ top, left });
      setIsReady(true);
    };
    const frame = window.requestAnimationFrame(positionTooltip);
    window.addEventListener('resize', positionTooltip);
    window.addEventListener('scroll', positionTooltip, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', positionTooltip);
      window.removeEventListener('scroll', positionTooltip, true);
    };
  }, [isVisible, position, content]);

  // Dynamic Theme Base Classes
  const themeClasses = theme === 'dark' 
    ? 'bg-[#1c1c1e] text-[#f9f9fb] shadow-xl' 
    : 'bg-white text-gray-900 border border-gray-200 shadow-lg';

  // Only render Tooltip wrapper and portal if there's actual content
  const hasContent = content && content !== '' && content !== null && content !== undefined;

  const events = {
      onMouseEnter: hasContent ? () => showTooltip() : undefined,
      onMouseLeave: hasContent ? () => hideTooltip() : undefined,
      onFocusCapture: hasContent ? () => showTooltip(0) : undefined,
      onBlurCapture: hasContent ? (event: React.FocusEvent<Element>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) hideTooltip(0);
      } : undefined,
      onPointerDown: hasContent ? (event: React.PointerEvent<Element>) => {
        if (event.pointerType === 'touch') isVisible ? hideTooltip(0) : showTooltip(0);
      } : undefined,
      onKeyDown: hasContent ? (event: React.KeyboardEvent<Element>) => {
        if (event.key === 'Escape' && isVisible) {
          event.stopPropagation();
          hideTooltip(0);
        }
      } : undefined,
  };
  const childProps = children.props as any;
  const description = [childProps['aria-describedby'], canInteract() && isVisible && tooltipId].filter(Boolean).join(' ') || undefined;
  const describedChild = React.cloneElement(children as React.ReactElement<any>, { 'aria-describedby': description });
  const childRef = (children as any).ref;
  const anchor = asChild ? React.cloneElement(children as React.ReactElement<any>, {
    'aria-describedby': description,
    className: [childProps.className, className].filter(Boolean).join(' ') || undefined,
    ref: (element: Element | null) => {
      wrapperRef.current = element;
      if (typeof childRef === 'function') childRef(element);
      else if (childRef) childRef.current = element;
    },
    ...Object.fromEntries(Object.entries(events).map(([name, handler]) => [name, (event: any) => {
      childProps[name]?.(event);
      handler?.(event);
    }])),
  }) : <div ref={element => { wrapperRef.current = element; }} className={`relative inline-flex overflow-visible ${className}`} {...events}>{describedChild}</div>;

  return (
    <>
      {anchor}

      {canInteract() && hasContent && isVisible &&
        ReactDOM.createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            ref={tooltipRef}
            className={`sprout-tooltip sprout-tooltip-${theme} fixed z-[999999999] max-w-[280px] ${themeClasses} text-[12px] font-medium tracking-[0.02em] text-left px-3.5 py-2 rounded-xl whitespace-normal pointer-events-none transition-opacity duration-200 ease-out motion-reduce:transition-none ${isReady ? 'opacity-100' : 'opacity-0'}`}
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
